// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import loading from '../../../../share/components/vue/ProcessingIcon';
import apiService from '../../../../share/util/api/service';
import handler from '../../../../share/util/apiResultValidator';
import { executePolling, getResponseCheckDefaultAction } from '../../../../share/util/api/polling';
import alert from '../../../../share/alert';
import { executeSyncRequest } from '../../../../share/util/api/syncRequest';
import { apiErrorDefaultAction } from "../../../../share/util/api/errorAction";
import logWrapper from '../../../../share/logWrapper';
import { pushFunction } from "../../../../router/obd";
import diagnoseResource from "../../../../share/util/diagnose";
import {
  FUNCTION_KEY_DTC,
  FUNCTION_KEY_P_DTC,
  FUNCTION_KEY_ONBOARD_SYSTEM_TEST,
  FUNCTION_KEY_VEHICLE_INFO,
  FUNCTION_KEY_ONBOARD_MONITOR_TEST,
  FUNCTION_KEY_DATA_MONITOR,
  FUNCTION_KEY_DIAGNOSE_FREQUENCY,
  FUNCTION_KEY_READINESS_STATUS,
} from "../../../../util/obd/functions";
import constants from "../../../../common/nodejs/constants";

export default {
  name: "obdIndividualMenu",
  data() {
    return {
      processing: false,
      isInitialized: false,
      communicationStandards: constants.UPPER_LAYER_PROTOCOL_SAEJ1979
    };
  },

  props: {
  },

  components: {
    'processing-view': loading,
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      obdDataId: state => state.obd.obdDataId,
      menuSupport(state) {
        const menuSupport = state.obd.menuSupport;
        if (menuSupport?.systemNo === this?.$route?.params?.systemNo) {
          // 自分のシステムと一致する場合のみ、メニューサポート有りと判断する
          return menuSupport?.value;
        }
        return null;
      },
    }),

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },

  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),
    ...mapActions('obd', ['updateObdMenuSupport']),

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * その他のエラー発生時の処理を行う
     * @param {string} action 処理内容
     */
    showUnexpectedError() {
      const messages = this.$t("fixed_resource.obd_function_common_message_result_ng_2");
      alert.showError(this, null,
        messages,
        () => {
          // なにもしない
        });
    },

    /**
     * OBD通信規格に従い機能ボタンを表示/非表示にする
     * J1979-1: DTC,パーマネントDTCを表示、それ以外を非表示
     * J1979-2: DTC,パーマネントDTC,オンボードモニタテスト,診断頻度,レディネスステータスを表示、それ以外を非表示
     */
    setFailedHidden() {
      this.updateObdMenuSupport({
        systemNo: this.$route.params.systemNo,
        value: {
          dataMonitor: false,
          diagCode: true,
          onboardMonitorTest: this.communicationStandards === constants.UPPER_LAYER_PROTOCOL_OBD_ON_UDS,
          onboardSystemTest: false,
          vehicleInfo: false,
          permanentDtc: true,
          diagnoseFrequency: this.communicationStandards === constants.UPPER_LAYER_PROTOCOL_OBD_ON_UDS,
          readinessStatus: this.communicationStandards === constants.UPPER_LAYER_PROTOCOL_OBD_ON_UDS
        }
      });
    },

    // 初期化通信
    async initCommunication() {
      try {
        this.processing = true;
        const response = await executePolling(
          this,
          // 呼び出したい POST API を実装
          async () =>
            await apiService.postCall(apiService.OBD, `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${this.$route.params.systemNo}/initialize`, {}, this.operationScreen),
          // 呼び出したい GET API を実装
          async (postApiResponse) =>
            await apiService.getCall(apiService.OBD, `/netApps/${this.netAppId}/requests/${postApiResponse?.data?.request_id}`, this.operationScreen),
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // GET API レスポンスチェックのコールバックの処理を実装
          (getApiResponse) => {
            if(getApiResponse?.data?.results?.communication_standards) {
              // レスポンスからOBD通信規格を取得
              this.communicationStandards = getApiResponse.data.results.communication_standards;
            }
            // ポーリングの GET レスポンスチェックのデフォルト処理
            return getResponseCheckDefaultAction(getApiResponse);
          },
          (result) => { this.validateErrorResult(result); },
          (result) => { this.validateErrorResult(result); }
        );
        if (response) {
          await this.getMenuConfig();
        } else {
          this.setFailedHidden();
        }
      } catch (error) {
        this.setFailedHidden();
      } finally {
        this.processing = false;
        this.isInitialized = true;
      }
    },

    // オンボードモニタテスト画面へ遷移
    openOnboardMonitorTest() {
      pushFunction(this, this.$route.params.systemNo, FUNCTION_KEY_ONBOARD_MONITOR_TEST);
    },

    // オンボードシステム画面へ遷移
    openObdOnboardSystemTest() {
      pushFunction(this, this.$route.params.systemNo, FUNCTION_KEY_ONBOARD_SYSTEM_TEST);
    },

    // ダイアグコード画面へ遷移
    openObdDtc() {
      // OBD個別診断DTCへ遷移
      pushFunction(this, this.$route.params.systemNo, FUNCTION_KEY_DTC);
    },

    // パーマネントダイアグコード画面へ遷移
    openObdPermanentDtc() {
      pushFunction(this, this.$route.params.systemNo, FUNCTION_KEY_P_DTC);
    },

    // データモニタ画面へ遷移
    openObdDataMonitor() {
      pushFunction(this, this.$route.params.systemNo, FUNCTION_KEY_DATA_MONITOR);
    },

    /**
     * 車両情報取得実行画面を表示する
     */
    showVehicleInformationAcquisition() {
      pushFunction(this, this.$route.params.systemNo, FUNCTION_KEY_VEHICLE_INFO);
    },

    /**
     * 診断頻度画面を表示する
     */
    showDiagnoseFrequency() {
      pushFunction(this, this.$route.params.systemNo, FUNCTION_KEY_DIAGNOSE_FREQUENCY);
    },

    /**
     * レディネスステータス画面を表示する
     */
    showReadinessStatus() {
      pushFunction(this, this.$route.params.systemNo, FUNCTION_KEY_READINESS_STATUS);
    },

    /**
     * メニュー一覧表示取得
     */
    async getMenuConfig() {
      // メニュー一覧表示の取得
      const response = await executeSyncRequest(
        this,
        async () => {
          return await apiService.getCall(
            apiService.OBD, 
            `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${this.$route.params.systemNo}/menuConfig`,
            this.operationScreen
          );
        },
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // GET API で正常レスポンスが返ってきた際の内容チェックはデフォルト処理を利用
        undefined,
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する
        (result) => { this.validateErrorResult(result); },
      );
      handler.validate(handler.validateTypes.all, response, null, null, () => {
        this.updateObdMenuSupport({
          systemNo: this.$route.params.systemNo,
          value: {
            dataMonitor: response.data.is_show_data_monitor,
            diagCode: response.data.is_show_dtc,
            onboardMonitorTest: response.data.is_show_onboard_monitor_test,
            onboardSystemTest: response.data.is_show_onboard_system_test,
            vehicleInfo: response.data.is_show_get_vehicle_info,
            permanentDtc: response.data.is_show_permanent_dtc,
            diagnoseFrequency: response.data.is_show_diagnose_frequency,
            readinessStatus: response.data.is_show_readiness_status
          }
        });
      }, (result) => {
        logWrapper.log('obd getMenuSupport api failed.', JSON.stringify(result));
        this.setFailedHidden();
      }, null, false);
    }
  },

  mounted() {

    if (!this.menuSupport) {
      // 既に該当システムのメニューサポートが store に存在しない場合のみ、初期化通信を行う
      this.initCommunication();
    } else {
      this.isInitialized = true;
    }

    // ツリーから機能ノードが選択されて遷移してきた時
    if (this.$route?.query?.funcKey && this.$route?.query?.systemNo) {
      // 選択された機能に遷移する
      pushFunction(this, this.$route.query.systemNo, this.$route.query.funcKey);
      return;
    }
  },
};
